<script lang="ts">
  import { SubscriptionService } from '$lib/services/subscription/SubscriptionService';
  import { LicenseFormattingService } from '$lib/services/subscription/LicenseFormattingService';
  import { useError } from '$lib/hooks/useError';
  import { errorService } from '$lib/services/ErrorService';
  
  export let onActivated: () => void;
  
  // Error handling setup
  const { tryExecute } = useError('LicenseActivationForm');
  
  let licenseKey = '';
  let isActivating = false;
  let activationMessage = '';
  let activationError = false;
  let messageTimer: number | null = null;
  let licenseInput: HTMLInputElement;
  
  // Auto-hide message after 5 seconds
  function showMessage(message: string, isError: boolean = false) {
    activationMessage = message;
    activationError = isError;
    
    // Clear existing timer
    if (messageTimer) {
      clearTimeout(messageTimer);
    }
    
    // Set new timer to clear message after 5 seconds
    messageTimer = setTimeout(() => {
      activationMessage = '';
      activationError = false;
      messageTimer = null;
    }, 5000);
  }
  
  function handleLicenseKeyInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const oldValue = licenseKey;
    const oldPosition = input.selectionStart || 0;

    // Remove any non-ASCII characters (including Korean) and only keep alphanumeric and hyphens
    const filtered = input.value
      .replace(/[^\x00-\x7F]/g, '') // Remove non-ASCII characters
      .replace(/[^A-Za-z0-9-]/g, ''); // Remove anything that's not alphanumeric or hyphen

    // Format the input
    licenseKey = LicenseFormattingService.formatLicenseKeyInput(filtered);

    // Update the input value directly to ensure Korean is removed
    if (input.value !== licenseKey) {
      input.value = licenseKey;
    }

    // Restore cursor position
    setTimeout(() => {
      if (licenseInput) {
        const newPosition = LicenseFormattingService.calculateCursorPosition(
          oldValue,
          licenseKey,
          oldPosition
        );
        licenseInput.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  }
  
  async function activateLicense() {
    const isValid = LicenseFormattingService.isValidFormat(licenseKey);

    if (!isValid) {
      errorService.handleValidationError('licenseKey', '올바른 라이센스 키 형식이 아닙니다. (예: XXXXXXXX-XXXXXXXX-XXXXXXXX-XXXXXXXX)');
      showMessage('올바른 라이센스 키 형식이 아닙니다. (예: XXXXXXXX-XXXXXXXX-XXXXXXXX-XXXXXXXX)', true);
      return;
    }
    
    isActivating = true;
    activationMessage = '';
    
    const [result, error] = await tryExecute(
      async () => {
        const service = SubscriptionService.getInstance();
        const response = await service.activateLicense(licenseKey);

        if (response.success) {
          errorService.showSuccess('라이센스가 성공적으로 활성화되었습니다!');
          showMessage('라이센스가 성공적으로 활성화되었습니다!', false);
          licenseKey = '';
          onActivated();
          return response;
        } else {
          // Extract and translate error message
          let errorMessage = '라이센스 활성화에 실패했습니다.';

          if (typeof response.error === 'object' && response.error !== null) {
            const message = response.error.message || '';

            // Translate common error messages to Korean
            if (message.includes('Invalid license') || message.includes('License not found')) {
              errorMessage = '유효하지 않은 라이센스 키입니다.';
            } else if (message.includes('already activated') || message.includes('maximum devices')) {
              errorMessage = '이 라이센스는 최대 디바이스 수에 도달했습니다.';
            } else if (message.includes('expired')) {
              errorMessage = '만료된 라이센스입니다.';
            } else if (message.includes('network') || message.includes('connection')) {
              errorMessage = '네트워크 연결 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
            } else if (message) {
              errorMessage = message;
            }
          }

          throw new Error(errorMessage);
        }
      },
      {
        severity: 'error',
        context: { action: 'activateLicense' },
        showToUser: false // We handle our own display
      }
    );
    
    if (error) {
      errorService.handleApiError(error, { component: 'LicenseActivationForm', action: 'activateLicense' });
      showMessage(error.message || '라이센스 활성화 중 오류가 발생했습니다.', true);
    }
    
    isActivating = false;
  }
  
  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !isActivating) {
      activateLicense();
    }
  }
  
  function handleBeforeInput(event: InputEvent) {
    // Don't block paste events
    if (event.inputType === 'insertFromPaste') {
      return;
    }

    // Block non-alphanumeric input (except hyphens for BETA keys) including Korean characters (for typing only)
    if (event.data && !/^[A-Za-z0-9-]$/.test(event.data)) {
      event.preventDefault();
    }
  }

  function handlePaste(event: ClipboardEvent) {
    event.preventDefault();

    // Get pasted text and trim whitespace
    const pastedText = (event.clipboardData?.getData('text') || '').trim();

    // Clean and format the pasted text
    const cleaned = pastedText
      .replace(/[^\x00-\x7F]/g, '') // Remove non-ASCII characters
      .replace(/[^A-Za-z0-9-]/g, ''); // Remove anything that's not alphanumeric or hyphen

    // Format the license key
    const formatted = LicenseFormattingService.formatLicenseKeyInput(cleaned);

    // Update the value
    licenseKey = formatted;

    // Manually update the input value
    if (licenseInput) {
      licenseInput.value = formatted;
    }
  }
</script>

<div class="activation-form">
  <div class="form-header">
    <h4 class="form-title">라이센스 키 입력</h4>
    <p class="form-description">Gumroad에서 구매한 라이센스 키를 입력하세요</p>
  </div>
  
  <div class="input-group">
    <input
      bind:this={licenseInput}
      type="text"
      value={licenseKey}
      on:input={handleLicenseKeyInput}
      on:keydown={handleKeydown}
      on:beforeinput={handleBeforeInput}
      on:paste={handlePaste}
      placeholder="XXXXXXXX-XXXXXXXX-XXXXXXXX-XXXXXXXX"
      class="license-input"
      disabled={isActivating}
      maxlength="35"
      pattern="[A-Z0-9\-]*"
    />
    <button 
      on:click={activateLicense} 
      disabled={isActivating || !licenseKey}
      class="activate-button"
    >
      {isActivating ? '활성화 중...' : '활성화'}
    </button>
  </div>
  
  {#if activationMessage}
    <div class="message {activationError ? 'error' : 'success'}">
      {activationMessage}
    </div>
  {/if}
</div>

<style>
  .activation-form {
    padding: 20px;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-primary);
    border-radius: 12px;
  }
  
  .form-header {
    margin-bottom: 16px;
  }
  
  .form-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 4px 0;
  }
  
  .form-description {
    font-size: 13px;
    color: var(--text-secondary);
    margin: 0;
  }
  
  .input-group {
    display: flex;
    gap: 8px;
  }
  
  .license-input {
    flex: 1;
    padding: 10px 12px;
    border: 1px solid var(--border-secondary);
    border-radius: 8px;
    font-size: 13px;
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace;
    background: var(--bg-primary);
    color: var(--text-primary);
    transition: border-color 0.2s;
  }
  
  .license-input:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
  
  .license-input:disabled {
    background: var(--bg-secondary);
    color: var(--text-tertiary);
    cursor: not-allowed;
  }
  
  .license-input::placeholder {
    color: var(--text-tertiary);
  }
  
  .activate-button {
    padding: 10px 20px;
    background: var(--accent-primary);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .activate-button:hover:not(:disabled) {
    background: var(--accent-secondary);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
  }
  
  .activate-button:disabled {
    background: var(--text-tertiary);
    cursor: not-allowed;
    transform: none;
  }
  
  .message {
    margin-top: 12px;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 13px;
    animation: slideDown 0.3s ease;
  }
  
  .message.success {
    background: rgba(16, 185, 129, 0.1);
    color: var(--success);
    border: 1px solid rgba(16, 185, 129, 0.2);
  }
  
  .message.error {
    background: rgba(239, 68, 68, 0.1);
    color: var(--error);
    border: 1px solid rgba(239, 68, 68, 0.2);
  }
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Dark mode support - only apply when app theme is dark */
  :global(.dark-theme) .activation-form {
    background: #111827;
    border-color: #374151;
  }
  
  :global(.dark-theme) .form-title {
    color: #f3f4f6;
  }
  
  :global(.dark-theme) .form-description {
    color: #9ca3af;
  }
  
  :global(.dark-theme) .license-input {
    background: #1f2937;
    color: #f3f4f6;
    border-color: #374151;
  }
  
  :global(.dark-theme) .license-input:focus {
    border-color: #818cf8;
    box-shadow: 0 0 0 3px rgba(129, 140, 248, 0.1);
  }
  
  :global(.dark-theme) .license-input:disabled {
    background: #111827;
    color: #6b7280;
  }
  
  :global(.dark-theme) .license-input::placeholder {
    color: #6b7280;
  }
  
  :global(.dark-theme) .activate-button {
    background: #6366f1;
  }
  
  :global(.dark-theme) .activate-button:hover:not(:disabled) {
    background: #818cf8;
    box-shadow: 0 2px 8px rgba(129, 140, 248, 0.3);
  }
  
  :global(.dark-theme) .activate-button:disabled {
    background: #4b5563;
  }
  
  :global(.dark-theme) .message.success {
    background: rgba(16, 185, 129, 0.15);
    color: #6ee7b7;
    border-color: rgba(16, 185, 129, 0.3);
  }
  
  :global(.dark-theme) .message.error {
    background: rgba(239, 68, 68, 0.15);
    color: #fca5a5;
    border-color: rgba(239, 68, 68, 0.3);
  }
</style>